import * as React from 'react';
import { CheckCircle2, GitBranch, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

export type EvidenceKind = 'locked' | 'premise' | 'verdict';

export interface EvidenceCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  kind?: EvidenceKind;
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
}

/**
 * EvidenceCard — workshop-note-style content card used inside the X-ray chat,
 * Engine gates, and Lab signal feeds. Carries the warm accent surface that
 * differentiates "this is locked evidence" from "this is regular content."
 */
export const EvidenceCard = React.forwardRef<HTMLDivElement, EvidenceCardProps>(
  ({ kind = 'locked', eyebrow, title, children, actions, className, ...rest }, ref) => {
    const Icon = kind === 'verdict' ? ShieldCheck : kind === 'premise' ? GitBranch : CheckCircle2;
    const eyebrowLabel =
      eyebrow ?? (kind === 'verdict' ? 'Verdict' : kind === 'premise' ? 'Premise' : 'Evidence locked');

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-fd border border-fd-accent-line p-4',
          'bg-gradient-to-b from-fd-accent-soft to-transparent',
          'grid gap-2',
          className,
        )}
        {...rest}
      >
        <header className="inline-flex items-center gap-2 font-geist-mono text-[10.5px] font-bold uppercase tracking-[0.08em] text-fd-text">
          <Icon className="h-3 w-3" />
          {eyebrowLabel}
        </header>
        {title && <div className="font-geist text-[13px] text-fd-text">{title}</div>}
        {children && <div className="font-geist text-[13px] text-fd-text leading-relaxed">{children}</div>}
        {actions && <div className="flex gap-2 pt-1">{actions}</div>}
      </div>
    );
  },
);
EvidenceCard.displayName = 'EvidenceCard';
